import { useMemo, useState } from 'react';
import { deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Copy, List, MessageSquareHeart, Phone, Save, Trash2 } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAdminCollection } from './hooks';
import { useSiteData } from '../../context/SiteDataContext';
import { BookingStatusBadge, EmptyState } from './adminUi';
import { BOOKING_STATUSES, VEHICLE_CLASSES, type Booking, type BookingStatus } from '../../lib/types';
import { formatDate, formatDateTimeMs, money } from '../../lib/format';

export default function BookingsAdmin() {
  const { items, loading, error } = useAdminCollection<Booking>('bookings');
  const { settings } = useSiteData();
  const [filter, setFilter] = useState<BookingStatus | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState('');
  const [view, setView] = useState<'list' | 'week'>('list');
  const [weekOffset, setWeekOffset] = useState(0);

  // Monday-start week for the calendar view
  const weekDays = useMemo(() => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return {
        iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
        label: d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }),
      };
    });
  }, [weekOffset]);

  const reviewMessage = (b: Booking) => {
    const link = settings.socials.google || 'https://apexcarcare.net/contact';
    return `Hi ${b.customer.name.split(' ')[0]}, thanks for choosing Apex Car Care! If you're happy with how the ${b.vehicle.make} turned out, a quick review would mean the world to us: ${link}`;
  };

  const filtered = useMemo(() => {
    const list = filter === 'all' ? items : items.filter((b) => b.status === filter);
    return [...list].sort((a, b) => b.date.localeCompare(a.date) || (b.createdAt ?? 0) - (a.createdAt ?? 0));
  }, [items, filter]);

  const setStatus = (b: Booking, status: BookingStatus) =>
    updateDoc(doc(db(), 'bookings', b.id), { status });

  const saveNotes = (b: Booking) =>
    updateDoc(doc(db(), 'bookings', b.id), { adminNotes: notesDraft[b.id] ?? b.adminNotes ?? '' });

  const remove = async (b: Booking) => {
    if (confirm(`Delete the booking for ${b.customer.name}? This can't be undone.`)) {
      await deleteDoc(doc(db(), 'bookings', b.id));
    }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="font-display text-2xl font-bold text-white">Bookings</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Confirm requests, track jobs through the day, and mark them complete.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="glass mr-2 flex rounded-full p-1">
          <button
            onClick={() => setView('list')}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1 text-sm font-semibold ${view === 'list' ? 'bg-gradient-to-r from-accent-600 to-sky-500 text-white' : 'text-zinc-400 hover:text-white'}`}
          >
            <List size={14} /> List
          </button>
          <button
            onClick={() => setView('week')}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1 text-sm font-semibold ${view === 'week' ? 'bg-gradient-to-r from-accent-600 to-sky-500 text-white' : 'text-zinc-400 hover:text-white'}`}
          >
            <CalendarDays size={14} /> Week
          </button>
        </div>
        {view === 'list' && (['all', ...BOOKING_STATUSES.map((s) => s.id)] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s as BookingStatus | 'all')}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-all ${
              filter === s ? 'bg-gradient-to-r from-accent-600 to-sky-500 text-white' : 'glass text-zinc-400 hover:text-white'
            }`}
          >
            {s === 'all' ? `All (${items.length})` : `${s.replace('_', ' ')} (${items.filter((b) => b.status === s).length})`}
          </button>
        ))}
      </div>

      {error && <p className="mt-6 text-sm text-red-400">Couldn't load bookings: {error}</p>}

      {view === 'week' && (
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => setWeekOffset(weekOffset - 1)}>
              <ChevronLeft size={14} /> Prev
            </button>
            <button className="text-sm font-semibold text-zinc-300 hover:text-white" onClick={() => setWeekOffset(0)}>
              {weekOffset === 0 ? 'This Week' : 'Back to This Week'}
            </button>
            <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => setWeekOffset(weekOffset + 1)}>
              Next <ChevronRight size={14} />
            </button>
          </div>
          <div className="mt-3 overflow-x-auto">
            <div className="grid min-w-[900px] grid-cols-7 gap-2">
              {weekDays.map((day) => {
                const dayBookings = items
                  .filter((b) => b.date === day.iso && b.status !== 'cancelled')
                  .sort((a, b) => a.timeSlot.localeCompare(b.timeSlot));
                const isToday = day.iso === new Date().toISOString().slice(0, 10);
                return (
                  <div key={day.iso} className={`card min-h-40 p-2.5 ${isToday ? 'border-accent-500/50' : ''}`}>
                    <p className={`text-xs font-bold ${isToday ? 'text-accent-300' : 'text-zinc-400'}`}>{day.label}</p>
                    <div className="mt-2 space-y-1.5">
                      {dayBookings.length === 0 ? (
                        <p className="text-[11px] text-zinc-600">—</p>
                      ) : (
                        dayBookings.map((b) => (
                          <button
                            key={b.id}
                            onClick={() => {
                              setView('list');
                              setFilter('all');
                              setOpenId(b.id);
                            }}
                            className={`block w-full rounded-lg border p-2 text-left text-[11px] leading-tight transition-colors hover:border-white/30 ${
                              b.status === 'pending'
                                ? 'border-amber-400/40 bg-amber-400/10'
                                : b.status === 'completed'
                                  ? 'border-emerald-400/30 bg-emerald-400/5'
                                  : 'border-sky-400/40 bg-sky-400/10'
                            }`}
                          >
                            <span className="font-bold text-white">{b.timeSlot}</span>
                            <span className="block truncate text-zinc-300">{b.customer.name}</span>
                            <span className="block truncate text-zinc-500">{b.packageName}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="mt-2 text-xs text-zinc-600">
            Amber = pending confirmation · Blue = confirmed/in progress · Green = completed. Click a booking to open it.
          </p>
        </div>
      )}

      {view === 'list' && (loading ? (
        <EmptyState text="Loading…" />
      ) : filtered.length === 0 ? (
        <EmptyState text="No bookings here yet." />
      ) : (
        <div className="mt-6 space-y-3">
          {filtered.map((b) => {
            const open = openId === b.id;
            return (
              <div key={b.id} className={`card overflow-hidden transition-colors ${open ? 'border-accent-500/40' : ''}`}>
                <button
                  className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 p-4 text-left sm:px-5"
                  onClick={() => setOpenId(open ? null : b.id)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-semibold text-white">
                      {b.customer.name}
                      <span className="ml-2 font-body text-sm font-normal text-zinc-400">{b.packageName}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-zinc-500">
                      {formatDate(b.date)} at {b.timeSlot} · {b.vehicle.year} {b.vehicle.make} {b.vehicle.model}
                    </p>
                  </div>
                  <span className="font-display font-bold text-white">{money(b.estimatedTotal)}</span>
                  <BookingStatusBadge status={b.status} />
                  <ChevronDown size={17} className={`text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>

                {open && (
                  <div className="border-t border-white/[0.06] p-5">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div className="space-y-2 text-sm">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">Customer</h3>
                        <p className="text-white">{b.customer.name}</p>
                        <p className="flex items-center gap-2 text-zinc-300">
                          <Phone size={13} className="text-accent-400" />
                          <a href={`tel:${b.customer.phone}`} className="hover:text-accent-300">{b.customer.phone}</a>
                        </p>
                        {b.customer.email && <p className="text-zinc-300">{b.customer.email}</p>}
                        <h3 className="pt-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Vehicle</h3>
                        <p className="text-zinc-300">
                          {b.vehicle.year} {b.vehicle.make} {b.vehicle.model} ·{' '}
                          {VEHICLE_CLASSES.find((v) => v.id === b.vehicle.vehicleClass)?.label}
                        </p>
                        <h3 className="pt-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Location</h3>
                        {b.serviceLocation === 'mobile' ? (
                          <>
                            <p className="text-zinc-300">
                              Mobile — {b.address}
                              {b.postalCode && <span className="uppercase">, {b.postalCode}</span>}
                            </p>
                            <p className="text-xs text-zinc-500">
                              {b.zoneName || 'No zone match'}
                              {b.travelFee > 0 && ` · travel fee ${money(b.travelFee)} (included in total)`}
                            </p>
                            {b.outsideArea && (
                              <p className="w-fit rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-amber-300">
                                Outside service area — confirm before accepting
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-zinc-300">Drop-off at shop</p>
                        )}
                        {b.addons?.length > 0 && (
                          <>
                            <h3 className="pt-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Add-ons</h3>
                            <ul className="space-y-1">
                              {b.addons.map((a) => (
                                <li key={a.id} className="flex justify-between text-zinc-300">
                                  <span>{a.name}</span><span>{money(a.price)}</span>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {b.promoCode && (
                          <>
                            <h3 className="pt-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Promo</h3>
                            <p className="text-zinc-300">
                              {b.promoCode} · −{money(b.discount ?? 0)} (total already reflects it)
                            </p>
                          </>
                        )}
                        {b.notes && (
                          <>
                            <h3 className="pt-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Customer Notes</h3>
                            <p className="whitespace-pre-wrap text-zinc-300">{b.notes}</p>
                          </>
                        )}
                        <p className="pt-2 text-xs text-zinc-600">Requested {b.createdAt ? formatDateTimeMs(b.createdAt) : '—'}</p>
                      </div>

                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">Status</h3>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {BOOKING_STATUSES.map((s) => (
                            <button
                              key={s.id}
                              onClick={() => setStatus(b, s.id)}
                              className={`rounded-lg border px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-all ${
                                b.status === s.id
                                  ? 'border-accent-400/70 bg-accent-500/15 text-white'
                                  : 'border-white/10 bg-white/[0.02] text-zinc-400 hover:border-white/25 hover:text-white'
                              }`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                        {b.status === 'completed' && (
                          <div className="mt-5 rounded-xl border border-emerald-400/25 bg-emerald-400/5 p-3.5">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-300">
                              Job done — ask for a review
                            </h3>
                            <div className="mt-2.5 flex flex-wrap gap-2">
                              <a
                                href={`sms:${b.customer.phone.replace(/[^0-9+]/g, '')}?&body=${encodeURIComponent(reviewMessage(b))}`}
                                className="btn-primary !px-4 !py-2 text-xs"
                              >
                                <MessageSquareHeart size={14} /> Text Review Request
                              </a>
                              <button
                                className="btn-ghost !px-4 !py-2 text-xs"
                                onClick={async () => {
                                  await navigator.clipboard.writeText(reviewMessage(b));
                                  setCopied(b.id);
                                  setTimeout(() => setCopied(''), 2000);
                                }}
                              >
                                <Copy size={13} /> {copied === b.id ? 'Copied!' : 'Copy Message'}
                              </button>
                            </div>
                            {!settings.socials.google && (
                              <p className="mt-2 text-[11px] text-zinc-500">
                                Tip: add your Google Business review link in Site Settings → Social Links so
                                reviews land on Google.
                              </p>
                            )}
                          </div>
                        )}

                        <h3 className="mt-5 text-xs font-bold uppercase tracking-wider text-zinc-500">Internal Notes</h3>
                        <textarea
                          className="input mt-2 min-h-24"
                          value={notesDraft[b.id] ?? b.adminNotes ?? ''}
                          onChange={(e) => setNotesDraft({ ...notesDraft, [b.id]: e.target.value })}
                          placeholder="Only visible to you — condition notes, deposits, etc."
                        />
                        <div className="mt-3 flex items-center justify-between">
                          <button className="btn-ghost !px-4 !py-2 text-xs" onClick={() => saveNotes(b)}>
                            <Save size={13} /> Save Notes
                          </button>
                          <button className="flex items-center gap-1.5 text-xs font-semibold text-red-400 hover:text-red-300" onClick={() => remove(b)}>
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
